import React from "react";
import ReactCountryFlag from "react-country-flag";
import { useTranslate } from "./TranslateContext";
import { Select, MenuItem, FormControl, Box } from "@mui/material";

const CONTROL_BG = "var(--color-green-soft)";
const CONTROL_TEXT = "var(--color-green-strong)";
const CONTROL_BORDER = "var(--color-green-strong)";
const CONTROL_MENU_HOVER = "rgba(36, 94, 67, 0.1)";
const CONTROL_MENU_SELECTED = "rgba(36, 94, 67, 0.16)";
const CONTROL_MENU_SELECTED_HOVER = "rgba(36, 94, 67, 0.22)";

const LanguagePicker = ({
  value,
  onChange,
  deferGlobalChange = false,
  fullWidth = false,
  minWidth = 160,
  sx = {},
  selectSx = {},
  menuPaperSx = {},
}) => {
  const { language, setLanguage, translate } = useTranslate();

  const selectedLanguage = value || language;

  const languages = [
    { code: "en", label: translate("english"), countryCode: "GB" },
    { code: "cs", label: translate("czech"), countryCode: "CZ" },
    { code: "fr", label: translate("french"), countryCode: "FR" },
    { code: "de", label: translate("german"), countryCode: "DE" },
    { code: "es", label: translate("spanish"), countryCode: "MX" },
    { code: "pt", label: translate("portuguese"), countryCode: "BR" },
    { code: "it", label: translate("italian"), countryCode: "IT" },
    { code: "pl", label: translate("polish"), countryCode: "PL" },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const handleChange = (event) => {
    const nextLanguage = event.target.value;

    if (typeof onChange === "function") {
      onChange(nextLanguage);
    }

    if (!deferGlobalChange) {
      setLanguage(nextLanguage);
    }
  };

  return (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth={fullWidth}
      sx={sx}
    >
      <Select
        value={selectedLanguage}
        onChange={handleChange}
        displayEmpty
        sx={{
          bgcolor: CONTROL_BG,
          backgroundColor: CONTROL_BG,
          color: CONTROL_TEXT,
          borderRadius: 2,
          minWidth: fullWidth ? "100%" : minWidth,
          fontWeight: 600,

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: CONTROL_BORDER,
            borderWidth: 1.5,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: CONTROL_BORDER,
            borderWidth: 2,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: CONTROL_BORDER,
            borderWidth: 2,
          },
          "& .MuiSelect-select": {
            color: CONTROL_TEXT,
            WebkitTextFillColor: CONTROL_TEXT,
            fontWeight: 600,
          },
          "& .MuiSelect-icon": {
            color: CONTROL_TEXT,
          },

          ...selectSx,
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              bgcolor: CONTROL_BG,
              backgroundColor: CONTROL_BG,
              color: CONTROL_TEXT,
              border: `1px solid ${CONTROL_BORDER}`,
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",

              "& .MuiMenu-list": {
                bgcolor: CONTROL_BG,
                backgroundColor: CONTROL_BG,
                color: CONTROL_TEXT,
                p: 0.5,
              },

              "& .MuiMenuItem-root": {
                bgcolor: CONTROL_BG,
                backgroundColor: CONTROL_BG,
                color: CONTROL_TEXT,
                borderRadius: 1,
                mx: 0.5,
                my: 0.25,
                "&:hover": {
                  bgcolor: CONTROL_MENU_HOVER,
                  backgroundColor: CONTROL_MENU_HOVER,
                },
                "&.Mui-selected": {
                  bgcolor: CONTROL_MENU_SELECTED,
                  backgroundColor: CONTROL_MENU_SELECTED,
                  color: CONTROL_TEXT,
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: CONTROL_MENU_SELECTED_HOVER,
                    backgroundColor: CONTROL_MENU_SELECTED_HOVER,
                  },
                },
              },

              ...menuPaperSx,
            },
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem key={lang.code} value={lang.code}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "inherit",
                fontWeight: "inherit",
              }}
            >
              <ReactCountryFlag
                countryCode={lang.countryCode}
                svg
                style={{
                  width: "1.4em",
                  height: "1.4em",
                  borderRadius: "3px",
                }}
                title={lang.label}
              />
              {lang.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguagePicker;